import React from 'react';
import { useState, useEffect, useRef } from 'react';
import firebase from '../../config/firebaseConfig';
import { useDispatch, useSelector } from 'react-redux';
import { ADMIN_LIST } from '../../config/adminConfig';
import { signOut } from '../../actions/authActions';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { Container, Card, Spinner, Image, Button, Form, Row, Col, Toast, ToastContainer, Modal } from 'react-bootstrap';
import SidebarDashboard from './SidebarDashboard';
import QuickNotesWidget from './QuickNotesWidget';
import defaultAvatar from '../../image/zen.jpg';
import { PHRASES_ZEN } from '../../config/zenparoles';
import '@animxyz/core';
import { XyzTransition } from '@animxyz/react';

const UserProfileFirestoreFull = () => {
  const auth = useSelector(state => state.firebase.auth);

  const [currentUser, setCurrentUser] = useState(null);
  const [role, setRole] = useState('membre');

  const [avatar, setAvatar] = useState('');
  const [name, setName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [city, setCity] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [email, setEmail] = useState('');

  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const [initialData, setInitialData] = useState({ avatar: '', name: '', firstName: '', city: '', zipCode: '', email: '' });

  const [showModal, setShowModal] = useState(false);

  const [showToast, setShowToast] = useState(false);
  const [toastVariant, setToastVariant] = useState('success');
  const [toastMessage, setToastMessage] = useState('');

  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const triggerToast = (message, variant = 'success') => {
    setToastMessage(message);
    setToastVariant(variant);
    setShowToast(true);
  };

  const handleOpenModal = () => setShowModal(true);

  const handleCloseModal = () => {
    if (showModal) {
      const timer = setTimeout(() => {
        setName(initialData.name);
        setFirstName(initialData.firstName);
        setCity(initialData.city || '');
        setZipCode(initialData.zipCode);
        setEmail(initialData.email);
      }, 300);
      setShowModal(false);

      return () => clearTimeout(timer);
    }
  };

  useEffect(() => {
    const unsubscribe = firebase.auth().onAuthStateChanged(async (user) => {
      if (user) {
        setCurrentUser(user);
        const currentEmail = user.email || '';
        setEmail(currentEmail);

        try {
          const docSnap = await firebase.firestore().collection('users').doc(user.uid).get();

          if (docSnap.exists) {
            const data = docSnap.data();
            const currentAvatar = data.avatar || defaultAvatar;
            const currentRole = data.role || 'membre';
            const currentName = data.name || '';
            const currentFirstName = data.firstName || '';
            const currentCity = data.city || '';
            const currentZipCode = data.zipCode || '';

            setAvatar(currentAvatar);
            setRole(currentRole);
            setName(currentName);
            setFirstName(currentFirstName);
            setCity(currentCity);
            setZipCode(currentZipCode);

            setInitialData({
              avatar: currentAvatar,
              name: currentName,
              firstName: currentFirstName,
              city: currentCity,
              zipCode: currentZipCode,
              email: currentEmail
            });
          }
        } catch (err) {
          triggerToast("Erreur lors de la lecture sur Firestore", 'danger');
          setAvatar(defaultAvatar);
        }
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleSaveChanges = async (e) => {
    e.preventDefault();
    if (!currentUser) return;

    try {
      setSaving(true);

      // 1. Si l'email a changé, on met d'abord à jour Firebase Auth
      if (email !== initialData.email) {
        try {
          await currentUser.updateEmail(email);
        } catch (authErr) {
          if (authErr.code === 'auth/requires-recent-login') {
            triggerToast("Sécurité : Veuillez vous déconnecter et vous reconnecter pour changer d'email.", 'danger');
            setSaving(false);
            return;
          }
          throw authErr;
        }
      }

      await firebase.firestore().collection('users').doc(currentUser.uid).set({
        name: name,
        firstName: firstName,
        city: city,
        zipCode: zipCode,
        email: email
      }, { merge: true });

      setTimeout(() => {
        setSaving(false);
        setShowModal(false);
        triggerToast("Profil mis à jour avec succès !", 'success');
        setInitialData({ avatar, name, firstName, city, zipCode, email });
      }, 1500);

    } catch (err) {
      console.error(err);
      triggerToast("Impossible d'enregistrer vos modifications.", 'danger');
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      setLoggingOut(true);
      triggerToast("Déconnexion en cours...", 'success');

      setTimeout(async () => {
        await firebase.auth().signOut();
        dispatch(signOut());
        setLoggingOut(false);
        navigate('/');
      }, 2000);
    } catch (err) {
      triggerToast("Erreur de déconnexion.", 'danger');
      setLoggingOut(false);
    }
  };

  const handleAvatarClick = () => fileInputRef.current.click();

  const cropImageToSquare = (base64Str) => {
    return new Promise((resolve) => {
      const img = new window.Image();
      img.src = base64Str;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = 400; // Résolution cible optimisée
        canvas.height = 400;

        const ctx = canvas.getContext('2d');

        // 1. Remplir le fond en blanc par défaut au cas où le dézoom dépasse
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, 400, 400);

        // 2. Déterminer la taille d'origine la plus courte (pour faire un carré)
        const sourceSize = Math.min(img.width, img.height);

        // Calcule le centrage d'origine
        const xOffset = (img.width - sourceSize) / 2;
        const yOffset = (img.height - sourceSize) / 2;

        // 3. Appliquer un facteur de zoom (0.85 = dézoomer pour laisser 15% d'espace autour)
        const zoomFactor = 1;
        const targetSize = canvas.width * zoomFactor;

        // Calcule la position de destination centrée sur le canvas
        const targetX = (canvas.width - targetSize) / 2;
        const shadowTargetY = (canvas.height - targetSize) / 2;

        // 4. Dessiner l'image recadrée et ajustée
        ctx.drawImage(
          img,
          xOffset, yOffset, sourceSize, sourceSize, // Zone découpée de l'image d'origine
          targetX, shadowTargetY, targetSize, targetSize // Zone dessinée sur le canvas final
        );

        resolve(canvas.toDataURL('image/jpeg', 0.85)); // Compression JPEG
      };
    });
  };

  const handleFileChange = (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const rawFile = files[0];

    if (!rawFile.type.startsWith('image/') && rawFile.type !== 'image/svg+xml') {
      triggerToast("Format de fichier non valide.", "danger");
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(rawFile);
    reader.onloadstart = () => { setUpdating(true); };
    reader.onloadend = async () => {
      try {
        let finalImage = reader.result;

        if (rawFile.type !== 'image/svg+xml') {
          finalImage = await cropImageToSquare(reader.result);
        }

        await firebase.firestore().collection('users').doc(currentUser.uid).set({
          avatar: finalImage
        }, { merge: true });

        setAvatar(finalImage);
        setInitialData(prev => ({ ...prev, avatar: finalImage }));
        triggerToast("Avatar mis à jour !", 'success');
      } catch (err) {
        console.error(err);
        triggerToast("Erreur lors du traitement de l'image.", 'danger');
      } finally {
        setUpdating(false);
      }
    };
  };

  return (
    <React.Fragment>
      <Container fluid className='min-vh-100 text-light py-4 mt-2 my-0'>
        <div className="d-flex flex-column flex-md-row">
          <SidebarDashboard />
          <div className="admin-main-wrapper w-100">
            <Container className="py-1 bg-profil min-vh-100">
              <XyzTransition appear xyz='fade stagger delay-4 duration-15'>
                <Container className="d-flex flex-column align-items-center my-4 my-sm-3 px-3 my-sm-3">
                  <h3 className="text-warning fw-semibold mb-3 fs-sm-2">🔐 Mon Profil</h3>
                  <Card className="shadow border-0 rounded-4 mt-0 p-1 p-sm-3 w-100" style={{ maxWidth: '460px' }}>
                    <Card.Body className="profile-body" style={{ height: 'auto' }}>
                      {loading && <div className="text-center"><Spinner animation="border" variant="primary" /></div>}

                      {!loading && currentUser && (
                        <React.Fragment>
                          <div className="text-center mb-4 select-none">
                            <div className="position-relative mb-2 d-inline-block" style={{ cursor: 'pointer' }} onClick={handleAvatarClick} title="Changer d'avatar">
                              <Image
                                src={avatar ? avatar : defaultAvatar}
                                roundedCircle
                                thumbnail
                                className={`shadow-sm border-2 border-warning bg-light p-2 ${updating ? 'opacity-50' : ''}`}
                                style={{ width: '110px', height: '110px', objectFit: 'cover' }}
                                alt="Avatar utilisateur"
                              />

                              {updating && (
                                <Spinner
                                  animation="border"
                                  size="sm"
                                  className="position-absolute top-50 start-50 translate-middle text-warning"
                                />
                              )}
                            </div>

                            {/* 👑 ÉTIQUETTE DE STATUT DYNAMIQUE */}
                            {(ADMIN_LIST.includes(auth.uid) || role === 'admin') ? (
                              <div className="mt-2">
                                <span
                                  className="badge bg-dark text-warning border border-2 border-warning px-3 py-2 mb-3 rounded-pill fw-semibold text-uppercase tracking-wider shadow-sm"
                                  style={{
                                    fontSize: '0.75rem',
                                    letterSpacing: '1px',
                                    boxShadow: '0 0 10px rgba(255, 193, 7, 0.2) !important'
                                  }}
                                >
                                  Administrateur
                                </span>
                              </div>
                            ) : (
                                // 👤 LOOK STANDARD : Un badge discret pour les utilisateurs classiques (optionnel)
                                <div className="mt-2">
                                  <span className="badge bg-secondary text-light px-3 py-2 mb-3 rounded-pill fw-semibold text-uppercase tracking-wider" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                                    Membre
                                  </span>
                                </div>
                              )}

                            <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/*" className="d-none" />

                            <div className="d-flex justify-content-center gap-2 mb-4">
                              <Button variant="outline-secondary" size="sm" className="rounded-pill px-3" onClick={handleAvatarClick} disabled={updating}>
                                <span>📁</span> Importer
                              </Button>
                            </div>

                            <h4 className="fw-semibold text-dark mb-1 fs-5 fs-sm-4">{initialData.firstName} {initialData.name}</h4>

                            <p className="text-muted mb-3">{initialData.email}</p>
                            {initialData.city && <p className="text-secondary"><span>📍</span> {initialData.city}</p>}
                          </div>

                          <div className="d-grid w-50 mx-auto">
                            <Button variant="primary" className="w-100 btn btn-primary rounded-3" onClick={handleOpenModal}>
                              Éditer le profil
                            </Button>
                            <Button variant="danger" className="w-100 btn btn-danger py-2 rounded-3 mt-3 mx-auto" onClick={handleLogout} disabled={saving || loggingOut}>
                              {loggingOut ? (
                                <React.Fragment>
                                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                                  Déconnexion en cours...
                                </React.Fragment>
                              ) : (
                                  <React.Fragment>
                                    Déconnexion
                                  </React.Fragment>
                                )}
                            </Button>
                          </div>
                        </React.Fragment>
                      )}
                    </Card.Body>
                  </Card>
                </Container>
              </XyzTransition>

              <Row className="justify-content-center px-2">
                <Col xs={12} md={6} className="mx-auto mb-4">
                  <QuickNotesWidget />
                </Col>
              </Row>

              <XyzTransition appear xyz='fade stagger delay-4 duration-15'>
                {(() => {
                  // 🎯 Récupère l'index de la phrase en fonction du jour de l'année (ex: jour 284 sur 365)
                  const indexDuJour = moment().dayOfYear() % PHRASES_ZEN.length;
                  const phraseZenDuJour = PHRASES_ZEN[indexDuJour];

                  return (
                    // {/* ⛩️ EN-TÊTE OU ENCADRÉ PENSÉE ZEN DU JOUR ✨ */ }
                    <Row className="justify-content-center px-2">
                      <Col xs={12} md={6} className="mx-auto mb-1">
                        <div className="d-flex justify-content-center">
                          <div className="card bg-dark border-secondary p-2 w-100 text-center mb-4 rounded-3 shadow-sm">
                            <div className="d-flex justify-content-center align-items-baseline mb-0 mt-1 pb-1 border-bottom border-secondary border-opacity-25">
                              <h6 className="text-warning small text-uppercase fw-semibold mb-2 style-titre-zen">
                                <span>⛩️</span> Pensée Zen du Jour <span>⛩️</span>
                              </h6>
                            </div>
                            <p className="mb-0 text-white-50 fst-italic border-bottom border-secondary border-opacity-25 px-1 py-2" style={{ fontSize: '0.95rem', lineHeight: '1.5' }}>
                              "{phraseZenDuJour}"
                            </p>
                            <span className="text-light small text-capitalize fw-semibold fst-italic mt-1">
                              {moment().format('dddd D MMMM')}
                            </span>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  );
                })()}
              </XyzTransition>

              <Modal show={showModal} onHide={handleCloseModal} centered animation={true} contentClassName="bg-dark text-light border-secondary" backdrop="static" dialogClassName="modal-dashboard-compensated" className="modal-fullscreen-sm-down" style={{marginLeft: window.innerWidth >= 768 ? '120px' : '0px'}}>
                <Modal.Header closeButton closeVariant="white" className="border-secondary">
                  <Modal.Title className="text-warning fw-semibold">Modifier mes informations</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSaveChanges}>
                  <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto', paddingBottom: '20px' }}>
                    <Form.Group className="mb-2" controlId="formName">
                      <Form.Label className="small fw-semibold text-secondary">Nom</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Votre nom"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="bg-secondary text-white border-0"
                        required
                      />
                    </Form.Group>

                    <Form.Group className="mb-2" controlId="formFirstName">
                      <Form.Label className="small fw-semibold text-secondary">Prénom</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Votre prénom"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className="bg-secondary text-white border-0"
                        required
                      />
                    </Form.Group>

                    <Form.Group className="mb-2" controlId="formCity">
                      <Form.Label className="small fw-semibold text-secondary">Ville</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Votre ville"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        className="bg-secondary text-white border-0"
                      />
                    </Form.Group>

                    <Form.Group className="mb-2" controlId="formZipCode">
                      <Form.Label className="small fw-semibold text-secondary">Code postal</Form.Label>
                      <Form.Control
                        type="text"
                        maxLength={5}
                        placeholder="Votre code postal"
                        value={zipCode}
                        onChange={(e) => setZipCode(e.target.value)}
                        className="bg-secondary text-white border-0"
                      />
                    </Form.Group>

                    <Form.Group className="mb-2" controlId="formEmail">
                      <Form.Label className="small fw-semibold text-secondary">Adresse Email</Form.Label>
                      <Form.Control
                        type="email"
                        placeholder="Email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="bg-secondary text-white border-0"
                        required
                      />
                    </Form.Group>
                  </Modal.Body>

                  <Modal.Footer className="d-flex justify-content-end border-secondary border-1 pt-3">
                    <Button variant="secondary" onClick={handleCloseModal} className="px-3 fw-semibold" disabled={saving}>
                      Annuler
                    </Button>
                    <Button variant="success" type="submit" className="px-3 fw-semibold" disabled={
                      (name === initialData.name &&
                        firstName === initialData.firstName &&
                        city === initialData.city && zipCode === initialData.zipCode && email === initialData.email) ||
                      saving ||
                      loggingOut
                    }
                    >
                      {saving ? (
                        <React.Fragment>
                          <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                          Enregistrement...
                        </React.Fragment>
                      ) : (
                          <React.Fragment>
                            Enregistrer votre profil
                          </React.Fragment>
                        )}
                    </Button>
                  </Modal.Footer>
                </Form>
              </Modal>

              {/* Toast Notifications */}
              <ToastContainer position="top-end" className="pt-5 px-3 pe-4" style={{ position: 'fixed', top: '10px', right: '0', zIndex: 99999 }}>
                <Toast
                  onClose={() => setShowToast(false)}
                  show={showToast}
                  delay={3000}
                  autohide
                  bg={toastVariant}
                  className="text-white shadow"
                >
                  <div className="d-flex align-items-center p-2">
                    <Toast.Body className="d-flex justify-content-between align-items-center py-2 px-3">
                      <i className="material-icons me-2" style={{ verticalAlign: 'middle' }}>
                        {toastVariant === 'success' ? 'check_circle' : 'error'}
                      </i>
                      <span className="fw-semibold" style={{ letterSpacing: '0.2px' }}>{toastMessage}</span>
                    </Toast.Body>
                    <Button
                      variant="close"
                      variant-white="true"
                      className="me-2 m-auto btn-close-white"
                      onClick={() => setShowToast(false)}
                      aria-label="Fermer"
                    />
                  </div>
                </Toast>
              </ToastContainer>
            </Container>
          </div>
        </div>
      </Container>
    </React.Fragment>

  );
};

export default UserProfileFirestoreFull;